import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Container, Row, Col, Navbar, Card, Form, Button, ButtonGroup } from 'react-bootstrap';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { OptionCalculator } from './utils/OptionCalculator';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

interface OptionInputs {
  underlying: number;
  strike: number;
  optionType: 'call' | 'put';
  volatility: number;
  expiryDate: string;
  interestRate: number;
  exerciseType: 'european' | 'american';
}

interface ChartData {
  labels: string[];
  datasets: {
    label: string;
    data: number[];
    borderColor: string;
    backgroundColor: string;
    tension: number;
  }[];
}

interface Greeks {
  delta: number;
  gamma: number;
  theta: number;
  vega: number;
  rho: number;
}

const App: React.FC = () => {
  const [inputs, setInputs] = useState<OptionInputs>({
    underlying: 100,
    strike: 100,
    optionType: 'call',
    volatility: 30,
    expiryDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 30 days from now
    interestRate: 5,
    exerciseType: 'european'
  });

  const [theoreticalValue, setTheoreticalValue] = useState<number | null>(null);
  const [greeks, setGreeks] = useState<Greeks | null>(null);
  const [chartType, setChartType] = useState<'expiry' | 'volatility' | 'underlying' | 'delta' | 'gamma' | 'vega' | 'theta'>('expiry');
  const [chartData, setChartData] = useState<ChartData | null>(null);

  const calculator = useMemo(() => new OptionCalculator(), []);

  const handleInputChange = (field: keyof OptionInputs, value: any) => {
    setInputs(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const calculateOption = () => {
    const timeToExpiry = (new Date(inputs.expiryDate).getTime() - new Date().getTime()) / (365.25 * 24 * 60 * 60 * 1000);
    
    if (timeToExpiry <= 0) {
      alert('Expiry date must be in the future');
      return;
    }

    let value: number;
    if (inputs.exerciseType === 'european') {
      value = calculator.blackScholes(
        inputs.underlying,
        inputs.strike,
        timeToExpiry,
        inputs.interestRate / 100,
        inputs.volatility / 100,
        inputs.optionType
      );
    } else {
      value = calculator.binomial(
        inputs.underlying,
        inputs.strike,
        timeToExpiry,
        inputs.interestRate / 100,
        inputs.volatility / 100,
        inputs.optionType,
        100 // number of steps
      );
    }

    setTheoreticalValue(value);
    
    // Calculate Greeks for both European and American options
    if (inputs.exerciseType === 'european') {
      const calculatedGreeks = calculator.calculateGreeks(
        inputs.underlying,
        inputs.strike,
        timeToExpiry,
        inputs.interestRate / 100,
        inputs.volatility / 100,
        inputs.optionType
      );
      setGreeks(calculatedGreeks);
    } else {
      // For American options, use numerical approximation
      const calculatedGreeks = calculator.calculateGreeksNumerical(
        inputs.underlying,
        inputs.strike,
        timeToExpiry,
        inputs.interestRate / 100,
        inputs.volatility / 100,
        inputs.optionType,
        100
      );
      setGreeks(calculatedGreeks);
    }
    
    generateChartData();
  };

  const generateChartData = useCallback(() => {
    const timeToExpiry = (new Date(inputs.expiryDate).getTime() - new Date().getTime()) / (365.25 * 24 * 60 * 60 * 1000);
    
    let labels: string[] = [];
    let dataPoints: number[] = [];
    let chartTitle = '';

    switch (chartType) {
      case 'expiry':
        chartTitle = 'Option Value vs Time to Expiry';
        for (let i = 0; i <= 30; i++) {
          // Use continuous time progression with minimum of 1 hour
          const t = Math.max(1 / (365 * 24), timeToExpiry * ((30 - i) / 30));
          const daysLabel = t * 365;
          
          if (daysLabel < 1) {
            labels.push(`${(daysLabel * 24).toFixed(1)}h`);
          } else {
            labels.push(`${daysLabel.toFixed(1)} days`);
          }
          
          let value: number;
          if (inputs.exerciseType === 'european') {
            value = calculator.blackScholes(
              inputs.underlying,
              inputs.strike,
              t,
              inputs.interestRate / 100,
              inputs.volatility / 100,
              inputs.optionType
            );
          } else {
            value = calculator.binomial(
              inputs.underlying,
              inputs.strike,
              t,
              inputs.interestRate / 100,
              inputs.volatility / 100,
              inputs.optionType,
              100
            );
          }
          dataPoints.push(value);
        }
        break;

      case 'volatility':
        chartTitle = 'Option Value vs Volatility';
        const baseVol = inputs.volatility;
        for (let i = -20; i <= 20; i++) {
          const vol = Math.max(1, baseVol + i);
          labels.push(`${vol.toFixed(0)}%`);
          
          let value: number;
          if (inputs.exerciseType === 'european') {
            value = calculator.blackScholes(
              inputs.underlying,
              inputs.strike,
              timeToExpiry,
              inputs.interestRate / 100,
              vol / 100,
              inputs.optionType
            );
          } else {
            value = calculator.binomial(
              inputs.underlying,
              inputs.strike,
              timeToExpiry,
              inputs.interestRate / 100,
              vol / 100,
              inputs.optionType,
              100
            );
          }
          dataPoints.push(value);
        }
        break;

      case 'underlying':
        chartTitle = 'Option Value vs Underlying Price';
        const basePrice = inputs.underlying;
        for (let i = -10; i <= 10; i++) {
          const price = basePrice * (1 + i / 100);
          labels.push(`$${price.toFixed(2)}`);
          
          let value: number;
          if (inputs.exerciseType === 'european') {
            value = calculator.blackScholes(
              price,
              inputs.strike,
              timeToExpiry,
              inputs.interestRate / 100,
              inputs.volatility / 100,
              inputs.optionType
            );
          } else {
            value = calculator.binomial(
              price,
              inputs.strike,
              timeToExpiry,
              inputs.interestRate / 100,
              inputs.volatility / 100,
              inputs.optionType,
              100
            );
          }
          dataPoints.push(value);
        }
        break;

      case 'delta':
        chartTitle = 'Delta vs Time to Expiry';
        for (let i = 0; i <= 30; i++) {
          // Use continuous time progression with minimum of 1 hour to avoid discontinuities
          const t = Math.max(1 / (365 * 24), timeToExpiry * ((30 - i) / 30));
          const daysLabel = t * 365;
          
          if (daysLabel < 1) {
            labels.push(`${(daysLabel * 24).toFixed(1)}h`);
          } else {
            labels.push(`${daysLabel.toFixed(1)} days`);
          }
          
          let greeksData;
          if (inputs.exerciseType === 'european') {
            greeksData = calculator.calculateGreeks(
              inputs.underlying,
              inputs.strike,
              t,
              inputs.interestRate / 100,
              inputs.volatility / 100,
              inputs.optionType
            );
          } else {
            greeksData = calculator.calculateGreeksNumerical(
              inputs.underlying,
              inputs.strike,
              t,
              inputs.interestRate / 100,
              inputs.volatility / 100,
              inputs.optionType,
              100
            );
          }
          dataPoints.push(greeksData.delta);
        }
        break;

      case 'gamma':
        chartTitle = 'Gamma vs Time to Expiry';
        for (let i = 0; i <= 30; i++) {
          // Use continuous time progression with minimum of 1 hour
          const t = Math.max(1 / (365 * 24), timeToExpiry * ((30 - i) / 30));
          const daysLabel = t * 365;
          
          if (daysLabel < 1) {
            labels.push(`${(daysLabel * 24).toFixed(1)}h`);
          } else {
            labels.push(`${daysLabel.toFixed(1)} days`);
          }
          
          let greeksData;
          if (inputs.exerciseType === 'european') {
            greeksData = calculator.calculateGreeks(
              inputs.underlying,
              inputs.strike,
              t,
              inputs.interestRate / 100,
              inputs.volatility / 100,
              inputs.optionType
            );
          } else {
            greeksData = calculator.calculateGreeksNumerical(
              inputs.underlying,
              inputs.strike,
              t,
              inputs.interestRate / 100,
              inputs.volatility / 100,
              inputs.optionType,
              100
            );
          }
          dataPoints.push(greeksData.gamma);
        }
        break;

      case 'vega':
        chartTitle = 'Vega vs Time to Expiry';
        for (let i = 0; i <= 30; i++) {
          // Use continuous time progression with minimum of 1 hour
          const t = Math.max(1 / (365 * 24), timeToExpiry * ((30 - i) / 30));
          const daysLabel = t * 365;
          
          if (daysLabel < 1) {
            labels.push(`${(daysLabel * 24).toFixed(1)}h`);
          } else {
            labels.push(`${daysLabel.toFixed(1)} days`);
          }
          
          let greeksData;
          if (inputs.exerciseType === 'european') {
            greeksData = calculator.calculateGreeks(
              inputs.underlying,
              inputs.strike,
              t,
              inputs.interestRate / 100,
              inputs.volatility / 100,
              inputs.optionType
            );
          } else {
            greeksData = calculator.calculateGreeksNumerical(
              inputs.underlying,
              inputs.strike,
              t,
              inputs.interestRate / 100,
              inputs.volatility / 100,
              inputs.optionType,
              100
            );
          }
          dataPoints.push(greeksData.vega);
        }
        break;

      case 'theta':
        chartTitle = 'Theta vs Time to Expiry';
        for (let i = 0; i <= 30; i++) {
          // Use continuous time progression with minimum of 1 hour
          const t = Math.max(1 / (365 * 24), timeToExpiry * ((30 - i) / 30));
          const daysLabel = t * 365;
          
          if (daysLabel < 1) {
            labels.push(`${(daysLabel * 24).toFixed(1)}h`);
          } else {
            labels.push(`${daysLabel.toFixed(1)} days`);
          }
          
          let greeksData;
          if (inputs.exerciseType === 'european') {
            greeksData = calculator.calculateGreeks(
              inputs.underlying,
              inputs.strike,
              t,
              inputs.interestRate / 100,
              inputs.volatility / 100,
              inputs.optionType
            );
          } else {
            greeksData = calculator.calculateGreeksNumerical(
              inputs.underlying,
              inputs.strike,
              t,
              inputs.interestRate / 100,
              inputs.volatility / 100,
              inputs.optionType,
              100
            );
          }
          dataPoints.push(greeksData.theta);
        }
        break;
    }

    // Determine chart color based on type
    let borderColor = 'rgb(75, 192, 192)';
    let backgroundColor = 'rgba(75, 192, 192, 0.2)';
    
    switch (chartType) {
      case 'delta':
        borderColor = 'rgb(255, 99, 132)';
        backgroundColor = 'rgba(255, 99, 132, 0.2)';
        break;
      case 'gamma':
        borderColor = 'rgb(54, 162, 235)';
        backgroundColor = 'rgba(54, 162, 235, 0.2)';
        break;
      case 'vega':
        borderColor = 'rgb(255, 205, 86)';
        backgroundColor = 'rgba(255, 205, 86, 0.2)';
        break;
      case 'theta':
        borderColor = 'rgb(153, 102, 255)';
        backgroundColor = 'rgba(153, 102, 255, 0.2)';
        break;
    }

    setChartData({
      labels,
      datasets: [
        {
          label: chartTitle,
          data: dataPoints,
          borderColor: borderColor,
          backgroundColor: backgroundColor,
          tension: 0.4,
        },
      ],
    });
  }, [chartType, inputs, calculator]);

  useEffect(() => {
    if (theoreticalValue !== null) {
      generateChartData();
    }
  }, [chartType, theoreticalValue, generateChartData]);

  const chartOptions = useMemo(() => {
    let yAxisTitle = 'Option Value ($)';
    let beginAtZero = true;
    
    switch (chartType) {
      case 'delta':
        yAxisTitle = 'Delta';
        beginAtZero = false;
        break;
      case 'gamma':
        yAxisTitle = 'Gamma';
        beginAtZero = false;
        break;
      case 'vega':
        yAxisTitle = 'Vega';
        beginAtZero = false;
        break;
      case 'theta':
        yAxisTitle = 'Theta';
        beginAtZero = false;
        break;
      case 'expiry':
      case 'volatility':
      case 'underlying':
      default:
        yAxisTitle = 'Option Value ($)';
        beginAtZero = true;
        break;
    }

    return {
      responsive: true,
      plugins: {
        legend: {
          position: 'top' as const,
        },
        title: {
          display: true,
          text: `Option Valuation - ${inputs.optionType.toUpperCase()} ${inputs.exerciseType.toUpperCase()}`,
        },
      },
      scales: {
        y: {
          beginAtZero: beginAtZero,
          title: {
            display: true,
            text: yAxisTitle
          }
        }
      }
    };
  }, [chartType, inputs.optionType, inputs.exerciseType]);

  return (
    <div className="App">
      <Navbar bg="dark" variant="dark" expand="lg" className="mb-4">
        <Container>
          <Navbar.Brand href="#home">
            <strong>Option Pricing Calculator</strong>
          </Navbar.Brand>
        </Container>
      </Navbar>

      <Container>
        <Row>
          <Col lg={4}>
            <Card className="calculation-panel">
              <Card.Header>
                <h4 className="mb-0">Option Parameters</h4>
              </Card.Header>
              <Card.Body>
                <Form>
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Underlying Price</Form.Label>
                        <Form.Control
                          type="number"
                          value={inputs.underlying}
                          onChange={(e) => handleInputChange('underlying', parseFloat(e.target.value) || 0)}
                          step="0.01"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Strike Price</Form.Label>
                        <Form.Control
                          type="number"
                          value={inputs.strike}
                          onChange={(e) => handleInputChange('strike', parseFloat(e.target.value) || 0)}
                          step="0.01"
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Form.Group className="mb-3">
                    <Form.Label>Option Type</Form.Label>
                    <div className="d-flex gap-3">
                      <Form.Check
                        type="radio"
                        label="Call"
                        name="optionType"
                        checked={inputs.optionType === 'call'}
                        onChange={() => handleInputChange('optionType', 'call')}
                      />
                      <Form.Check
                        type="radio"
                        label="Put"
                        name="optionType"
                        checked={inputs.optionType === 'put'}
                        onChange={() => handleInputChange('optionType', 'put')}
                      />
                    </div>
                  </Form.Group>

                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Volatility (%)</Form.Label>
                        <Form.Control
                          type="number"
                          value={inputs.volatility}
                          onChange={(e) => handleInputChange('volatility', parseFloat(e.target.value) || 0)}
                          min="0"
                          max="100"
                          step="0.1"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Interest Rate (%)</Form.Label>
                        <Form.Control
                          type="number"
                          value={inputs.interestRate}
                          onChange={(e) => handleInputChange('interestRate', parseFloat(e.target.value) || 0)}
                          step="0.01"
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Form.Group className="mb-3">
                    <Form.Label>Expiry Date</Form.Label>
                    <Form.Control
                      type="date"
                      value={inputs.expiryDate}
                      onChange={(e) => handleInputChange('expiryDate', e.target.value)}
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Exercise Type</Form.Label>
                    <div className="d-flex gap-3">
                      <Form.Check
                        type="radio"
                        label="European"
                        name="exerciseType"
                        checked={inputs.exerciseType === 'european'}
                        onChange={() => handleInputChange('exerciseType', 'european')}
                      />
                      <Form.Check
                        type="radio"
                        label="American"
                        name="exerciseType"
                        checked={inputs.exerciseType === 'american'}
                        onChange={() => handleInputChange('exerciseType', 'american')}
                      />
                    </div>
                  </Form.Group>

                  <Button 
                    className="btn-calculate w-100 mb-3" 
                    onClick={calculateOption}
                    size="lg"
                  >
                    Calculate Option Value
                  </Button>

                  {theoreticalValue !== null && (
                    <div className="result-value">
                      Theoretical Value: ${theoreticalValue.toFixed(4)}
                    </div>
                  )}

                  {greeks && (
                    <div className="greeks-display mt-3">
                      <h6 style={{color: 'white', marginBottom: '15px'}}>
                        Option Greeks {inputs.exerciseType === 'american' && '(Numerical)'}
                      </h6>
                      <Row>
                        <Col xs={6}>
                          <div className="greek-item">
                            <strong>Delta:</strong> {greeks.delta.toFixed(4)}
                          </div>
                          <div className="greek-item">
                            <strong>Gamma:</strong> {greeks.gamma.toFixed(4)}
                          </div>
                        </Col>
                        <Col xs={6}>
                          <div className="greek-item">
                            <strong>Vega:</strong> {greeks.vega.toFixed(4)}
                          </div>
                          <div className="greek-item">
                            <strong>Theta:</strong> {greeks.theta.toFixed(4)}
                          </div>
                        </Col>
                      </Row>
                      {inputs.exerciseType === 'american' && (
                        <div style={{fontSize: '0.8rem', color: '#FFD700', marginTop: '10px'}}>
                          * Greeks for American options calculated using numerical approximation
                        </div>
                      )}
                    </div>
                  )}
                </Form>
              </Card.Body>
            </Card>
          </Col>

          <Col lg={8}>
            <div className="chart-container">
              <h4 className="mb-3">Sensitivity Analysis</h4>
              
              <ButtonGroup className="mb-3 w-100">
                <Button
                  variant={chartType === 'expiry' ? 'primary' : 'outline-primary'}
                  onClick={() => setChartType('expiry')}
                >
                  Time Decay
                </Button>
                <Button
                  variant={chartType === 'volatility' ? 'primary' : 'outline-primary'}
                  onClick={() => setChartType('volatility')}
                >
                  Volatility
                </Button>
                <Button
                  variant={chartType === 'underlying' ? 'primary' : 'outline-primary'}
                  onClick={() => setChartType('underlying')}
                >
                  Underlying Price
                </Button>
              </ButtonGroup>
              
              <ButtonGroup className="mb-3 w-100">
                <Button
                  variant={chartType === 'delta' ? 'primary' : 'outline-primary'}
                  onClick={() => setChartType('delta')}
                >
                  Delta
                </Button>
                <Button
                  variant={chartType === 'gamma' ? 'primary' : 'outline-primary'}
                  onClick={() => setChartType('gamma')}
                >
                  Gamma
                </Button>
                <Button
                  variant={chartType === 'vega' ? 'primary' : 'outline-primary'}
                  onClick={() => setChartType('vega')}
                >
                  Vega
                </Button>
                <Button
                  variant={chartType === 'theta' ? 'primary' : 'outline-primary'}
                  onClick={() => setChartType('theta')}
                >
                  Theta
                </Button>
              </ButtonGroup>

              {chartData ? (
                <>
                  <Line data={chartData} options={chartOptions} />
                  {(chartType === 'delta' || chartType === 'gamma' || chartType === 'vega' || chartType === 'theta') && (
                    <div className="mt-3 p-3" style={{backgroundColor: '#f8f9fa', borderRadius: '10px', border: '1px solid #dee2e6'}}>
                      <h6 style={{color: '#495057', marginBottom: '10px'}}>📊 Understanding Greeks behavior near expiration:</h6>
                      <div style={{fontSize: '0.9rem', color: '#6c757d'}}>
                        {chartType === 'delta' && (
                          <p><strong>Delta:</strong> Shows smooth acceleration towards final payoff values as time approaches expiration. The curve naturally approaches 1 (ITM calls), 0 (OTM calls), -1 (ITM puts), or 0 (OTM puts).</p>
                        )}
                        {chartType === 'gamma' && (
                          <p><strong>Gamma:</strong> Increases dramatically as expiration approaches, especially near the strike price. This reflects the rapid change in delta sensitivity as the option approaches its binary payoff.</p>
                        )}
                        {chartType === 'vega' && (
                          <p><strong>Vega:</strong> Decreases smoothly to near zero as expiration approaches because volatility has diminishing impact on option value when little time remains.</p>
                        )}
                        {chartType === 'theta' && (
                          <p><strong>Theta:</strong> Shows accelerating time decay as expiration nears. The steepening curve reflects how time value erodes faster in the final hours before expiration.</p>
                        )}
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-5 text-muted">
                  <h5>Click "Calculate Option Value" to generate charts</h5>
                  <p>The chart will show how the option value changes based on different parameters.</p>
                </div>
              )}
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default App;
